// hack; https://github.com/aws/aws-sdk-js-v3/issues/6636#issuecomment-2521743851
import { PutObjectCommand, S3Client } from "@aws-sdk/client-s3";
import { v4 as uuidv4 } from "uuid";

import { fetchImageAsBytes } from "../../utils/files";
import UserApiBase from "../base/UserApiBase";

import ResourceService, { type S3UploadCreds } from "./resource";

import { calculateImageHash } from "@/utils/image/images";
import { type AppUserSession } from "@/schemas/auth/session";
import {
  type PaginatedRequest,
  type PaginatedResponse,
} from "@/schemas/api/pagination";
import {
  type ImageMetaData,
  type UserImageMedia,
  UserImageType,
  type UserLikableImageMedia,
  UserLikableImageMediaSchema,
} from "@/schemas/user/image";

if (typeof Blob !== "undefined" && !Blob.prototype.arrayBuffer) {
  Blob.prototype.arrayBuffer = function (): Promise<ArrayBuffer> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = (): void => {
        resolve(reader.result as ArrayBuffer);
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(this);
    });
  };
}

class MediaService extends UserApiBase {
  private userSession: AppUserSession;
  private resourceService: ResourceService;

  public constructor(session: AppUserSession) {
    super("/media", session);
    this.userSession = session;
    this.resourceService = new ResourceService(this.userSession);
  }

  private getS3Client(creds: S3UploadCreds): S3Client {
    const s3 = new S3Client({
      credentials: {
        accessKeyId: creds.access_key,
        secretAccessKey: creds.secret_key,
        sessionToken: creds.session_token,
      },
      region: creds.region,
    });
    return s3;
  }

  public async getImages(
    username: string,
    type: UserImageType,
    page: PaginatedRequest,
  ): Promise<PaginatedResponse<UserLikableImageMedia>> {
    const paginateQueryStr = this.buildPaginateQueryStr(page);
    return this.get(`/images/${username}/${type}${paginateQueryStr}`);
  }

  public async getImageMediaByImageId(
    imageId: string,
  ): Promise<UserLikableImageMedia> {
    const imageMedia = await this.get(`/image/${imageId}`);
    const validatedImageMedia = UserLikableImageMediaSchema.parse(imageMedia);
    return validatedImageMedia;
  }

  public async uploadPublicImages(
    imageUris: string[],
    imageType: UserImageType,
  ): Promise<PaginatedResponse<UserImageMedia>> {
    const events = await Promise.all(
      imageUris.map(async (uri) => {
        const metaData = await this.uploadImageToS3(uri, imageType);
        return metaData;
      }),
    );
    return this.post("/images", { events });
  }

  private async uploadImageToS3(
    imageUri: string,
    imageType: UserImageType,
  ): Promise<ImageMetaData> {
    const uuid = uuidv4();
    const imageHash = calculateImageHash();
    const imageBytes = await fetchImageAsBytes(imageUri);

    const imageS3Key = `${this.userSession.identity.username}/images/${uuid}/raw.jpg`;
    const creds = await this.resourceService.getS3Resource();
    const s3 = this.getS3Client(creds);

    const params = {
      Body: imageBytes,
      Bucket: creds.bucket_name,
      ContentType: "image/jpeg",
      Key: imageS3Key,
    };

    const command = new PutObjectCommand(params);
    await s3.send(command);

    return { imageHash, imageS3Key, imageType };
  }

  public async deleteImage(imageId: string): Promise<void> {
    return this.delete(`/images/${imageId}`);
  }
}

export default MediaService;
